import { useEffect } from "react";

/**
 * Lightweight SEO helper — sets document.title + meta description + canonical
 * + og:title / og:description / og:image without external deps.
 * Restores previous values on unmount so navigating away doesn't leak meta.
 */
export default function useDocumentMeta({ title, description, image, canonical } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    const restores = []; // { el, attr, prev, created }

    const upsert = (selector, build, attr, value) => {
      if (!value) return;
      let el = document.head.querySelector(selector);
      const created = !el;
      if (created) {
        el = build();
        document.head.appendChild(el);
      }
      const prev = el.getAttribute(attr);
      el.setAttribute(attr, value);
      restores.push({ el, attr, prev, created });
    };

    const setMeta = (key, value, attrName = "name") => {
      upsert(
        `meta[${attrName}="${key}"]`,
        () => {
          const m = document.createElement("meta");
          m.setAttribute(attrName, key);
          return m;
        },
        "content",
        value
      );
    };
    const setLink = (rel, href) => {
      upsert(
        `link[rel="${rel}"]`,
        () => {
          const l = document.createElement("link");
          l.setAttribute("rel", rel);
          return l;
        },
        "href",
        href
      );
    };

    if (title) document.title = title;
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:image", image, "property");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);
    setLink("canonical", canonical);

    return () => {
      document.title = prevTitle;
      restores.forEach(({ el, attr, prev, created }) => {
        if (created) el.remove();
        else if (prev !== null && prev !== undefined) el.setAttribute(attr, prev);
      });
    };
  }, [title, description, image, canonical]);
}
